// components/ui/landing/Navigation.tsx
'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ChevronDown, ChevronUp } from "flowbite-react-icons/outline";
import LogoShort from "@/components/ui/LogoShort";
import LogoWortmarke from "@/components/ui/LogoWortmarke";

// --- MODIFICATION 1: Add the new prop to the type definition ---
type NavigationProps = {
  className?: string;
  showGuideLinkInMobileMenu?: boolean; // Prop to control the mobile link visibility
};

// --- MODIFICATION 2: Receive and destructure the new prop ---
const Navigation: React.FC<NavigationProps> = ({ className = '', showGuideLinkInMobileMenu = false }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const pathname = usePathname();
  const sidebarRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const staticCategories = [
    { id: '67228dab997a0f057907f09b', name: 'Spirituelles Coaching' },
    { id: '672285ca997a0f057907f099', name: 'Tarot & Kartenlegen' },
    { id: '6722998b997a0f057907f0b2', name: 'Lebenskrisenbewältigung' },
    { id: '67229639997a0f057907f09e', name: 'Liebe & Partnerschaft' },
    { id: '67229678997a0f057907f0a0', name: 'Persönliches Wachstum & Transformation' },
  ];

  // Close menus on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsCategoryDropdownOpen(false);
  }, [pathname]);

  // Close category dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isCategoryDropdownOpen && dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsCategoryDropdownOpen(false);
      }
      // Close mobile menu on click outside the sidebar
      if (isMobileMenuOpen && sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
        // Check if the click was on the hamburger button itself
        const hamburgerButton = document.querySelector('[aria-label="Hauptmenü"]');
        if (!hamburgerButton || !hamburgerButton.contains(event.target as Node)) {
          // setIsMobileMenuOpen(false); // Keep this commented if overlay click should close it
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isCategoryDropdownOpen, isMobileMenuOpen]); // Added isMobileMenuOpen dependency

  // Close menus on Escape key press
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMobileMenuOpen(false);
        setIsCategoryDropdownOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  const closeMobileMenu = () => setIsMobileMenuOpen(false);
  const toggleCategoryDropdown = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering click outside listener
    setIsCategoryDropdownOpen(!isCategoryDropdownOpen);
  };

  // Helper for category dropdown button content
  const categoryDisplay = (
    <>
      <span>Kategorien</span>
      {isCategoryDropdownOpen ? <ChevronUp className="ml-1 w-4 h-4" /> : <ChevronDown className="ml-1 w-4 h-4" />}
    </>
  );

  return (
    <nav className={`${className}`}>
      {/* Desktop Navigation */}
      <div className="hidden lg:flex items-center space-x-6">
        {/* Desktop Links */}
        <Link href="/verfuegbar-jetzt" className="text-gray-700 hover:text-primary px-2 py-1 rounded-md transition duration-150 ease-in-out">
          <span className="font-medium text-primary">Sofort verfügbare Beratungen</span>
        </Link>

        <div className="relative" ref={dropdownRef}>
          <button
            className="flex items-center text-gray-700 hover:text-primary px-2 py-1 rounded-md transition duration-150 ease-in-out"
            onClick={toggleCategoryDropdown}
            aria-expanded={isCategoryDropdownOpen}
            aria-haspopup="true"
            type="button"
          >
            {categoryDisplay}
          </button>

          {isCategoryDropdownOpen && (
            <div className="absolute z-10 left-0 mt-2 w-64 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5" role="menu">
              <div className="py-1">
                {staticCategories.map((category) => (
                  <Link
                    key={category.id}
                    href={`/category/${category.id}`}
                    className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 hover:text-primary"
                    role="menuitem"
                    onClick={() => setIsCategoryDropdownOpen(false)} // Close dropdown on click
                  >
                    {category.name}
                  </Link>
                ))}
                <Link
                  href="/categories"
                  className="block px-4 py-3 text-sm font-medium text-primary hover:bg-gray-100"
                  role="menuitem"
                  onClick={() => setIsCategoryDropdownOpen(false)} // Close dropdown on click
                >
                  Mehr Kategorien anzeigen
                </Link>
              </div>
            </div>
          )}
        </div>

        <Link href="/budget-offers" className="text-gray-700 hover:text-primary px-2 py-1 rounded-md transition duration-150 ease-in-out">
          Kennenlern-Angebote
        </Link>
        <Link href="/products" className="text-gray-700 hover:text-primary px-2 py-1 rounded-md transition duration-150 ease-in-out">
          Beratungen
        </Link>
        <Link href="/guides" className="text-gray-700 hover:text-primary px-2 py-1 rounded-md transition duration-150 ease-in-out">
          Guides
        </Link>
      </div>

      {/* Mobile Hamburger Button */}
      <button
        className="lg:hidden p-2 rounded-md text-gray-700 hover:text-primary hover:bg-gray-100 focus:outline-none"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        aria-expanded={isMobileMenuOpen}
        aria-label="Hauptmenü"
        type="button"
      >
        <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isMobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
        </svg>
      </button>

      {/* Mobile Overlay */}
      <div
        className={`fixed inset-0 bg-gray-600 bg-opacity-75 z-40 transition-opacity duration-300 ${isMobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={closeMobileMenu} // Close menu when overlay is clicked
        aria-hidden="true"
      />

      {/* Mobile Sidebar */}
      <div
        ref={sidebarRef}
        className={`fixed inset-y-0 left-0 flex flex-col w-72 max-w-sm bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}
        role="dialog" // Added role for accessibility
        aria-modal="true" // Added aria-modal for accessibility
        aria-labelledby="mobile-menu-title" // Added aria-labelledby for accessibility
      >
        {/* Sidebar Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          {/* Use an h2 for the title, visually hidden but accessible */}
          <h2 id="mobile-menu-title" className="sr-only">Hauptmenü</h2>
          {/* Logos */}
          <div className="md:hidden">
            <LogoShort size={40} />
          </div>
          <div className="hidden md:block">
            <LogoWortmarke size={40} />
          </div>
          {/* Close Button */}
          <button
            className="p-2 rounded-md text-gray-400 hover:text-primary hover:bg-gray-100"
            onClick={closeMobileMenu}
            aria-label="Menü schließen"
            type="button"
          >
            <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Sidebar Content */}
        <div className="flex-1 overflow-y-auto px-4 py-4">

          {/* Categories Section */}
          <Link
            href="/categories"
            className="inline-flex items-center text-md font-medium text-primary mb-2 hover:underline"
            onClick={closeMobileMenu}
          >
            <h3>Kategorien</h3>
          </Link>
          <div className="space-y-1">
            {staticCategories.map((category) => (
              <Link
                key={category.id}
                href={`/category/${category.id}`}
                className="block px-2 py-3 text-base font-medium text-gray-700 hover:text-primary hover:bg-gray-50 rounded-md"
                onClick={closeMobileMenu}
              >
                {category.name}
              </Link>
            ))}
            <Link
              href="/categories"
              className="block px-2 py-3 text-base font-medium text-primary hover:bg-gray-50 rounded-md"
              onClick={closeMobileMenu}
            >
              Mehr Kategorien anzeigen →
            </Link>
          </div>

          {/* Separator */}
          <div className="border-t border-gray-200 my-4" />

          {/* Main Links Section */}
          <div className="space-y-1">
            <Link
              href="/so-funktioniert-guideful"
              className="inline-flex items-center text-md font-medium text-primary mb-2 hover:underline"
              onClick={closeMobileMenu}
            >
              <h3>So funktioniert guideful</h3>
            </Link>
            <Link
              href="/verfuegbar-jetzt"
              className="block px-2 py-3 text-base font-bold text-primary hover:bg-gray-50 rounded-md" // Removed mt-2 as separator handles spacing
              onClick={closeMobileMenu}
              aria-label="Sofort Beratung verfügbar – Berater die jetzt online sind"
              title="Sofort Beratung verfügbar – Berater die jetzt online sind"
            >
              Sofort verfügbare Angebote
            </Link>
            <Link
              href="/budget-offers"
              className="block px-2 py-3 text-base font-medium text-gray-700 hover:text-primary hover:bg-gray-50 rounded-md"
              onClick={closeMobileMenu}
            >
              Kennenlern-Angebote
            </Link>
            <Link
              href="/products"
              className="block px-2 py-3 text-base font-medium text-gray-700 hover:text-primary hover:bg-gray-50 rounded-md"
              onClick={closeMobileMenu}
            >
              Beratungen
            </Link>
            <Link
              href="/guides"
              className="block px-2 py-3 text-base font-medium text-gray-700 hover:text-primary hover:bg-gray-50 rounded-md"
              onClick={closeMobileMenu}
            >
              Guides
            </Link>
          </div>

          {/* --- MODIFICATION 3: Conditionally render "Guide werden" link --- */}
          {showGuideLinkInMobileMenu && (
            <>
              {/* Separator */}
              <div className="border-t border-gray-200 my-4" />
              {/* Link */}
              <div className="space-y-1">
                <Link
                  href="https://guidewerden.guideful.de"
                  className="block px-2 py-3 text-base font-medium text-primary hover:bg-gray-50 rounded-md" // Styled similar to other links, but with primary color
                  onClick={closeMobileMenu} // Ensure menu closes on click
                >
                  Guide werden
                </Link>
              </div>
            </>
          )}
          {/* Add Login/Register or Account links here if needed for mobile */}

        </div>
      </div>
    </nav>
  );
};

export default Navigation;
